import React, { useState } from "react";
import { Loader2, Send, ShieldAlert, X } from "lucide-react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/sonner";
import api, { formatApiError } from "@/lib/api";
import TemplatePicker from "@/components/messaging/TemplatePicker";

/**
 * Employer-initiated conversation starter. Opens POST /messaging/conversations.
 * Surfaces 403 scam-safety and rate-limit errors cleanly so the employer
 * gets the right nudge instead of a raw stack trace.
 */
export const MessageComposeDialog = ({
    open,
    onOpenChange,
    talentUserId,
    talentLabel = "this candidate",
    jobId = null,
    onSent,
}) => {
    const [body, setBody] = useState("");
    const [sending, setSending] = useState(false);

    const close = () => { setBody(""); onOpenChange(false); };

    const send = async () => {
        const trimmed = body.trim();
        if (!trimmed) return;
        setSending(true);
        try {
            const { data } = await api.post("/messaging/conversations", {
                talent_user_id: talentUserId,
                body: trimmed,
                job_id: jobId || undefined,
            });
            toast.success(data.reopened ? "Message sent — this re-opened an existing thread." : "Message sent.");
            if (data.flagged_for_review) {
                toast("Flagged for admin review (contains terms we check for safety).", {
                    icon: <ShieldAlert size={14} className="text-amber-500" />,
                });
            }
            onSent?.(data);
            close();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSending(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(true) : close())}>
            <DialogContent data-testid="msg-compose-dialog" className="max-w-lg">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Send size={16} className="text-emerald-500" />
                        Message {talentLabel}
                    </DialogTitle>
                    <DialogDescription>
                        Keep the first message short and personal — identical bulk messages are blocked.
                        Full contact details stay hidden until the candidate opts in.
                    </DialogDescription>
                </DialogHeader>
                <Textarea
                    rows={6}
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    placeholder="Hi — I'm hiring a Senior CS Lead and your profile stood out because..."
                    data-testid="msg-compose-body"
                    className="mt-2"
                    maxLength={4000}
                />
                <div className="mt-1 flex justify-between items-center">
                    <TemplatePicker onInsert={(text) => setBody(text)} />
                    <span className="text-[11px] text-muted-foreground">{body.length}/4000</span>
                </div>
                <DialogFooter className="mt-4">
                    <button
                        onClick={close}
                        className="btn-outline text-sm px-4 py-2 inline-flex items-center gap-1"
                        data-testid="msg-compose-cancel"
                    >
                        <X size={13} /> Cancel
                    </button>
                    <button
                        onClick={send}
                        disabled={sending || !body.trim()}
                        className="btn-green text-sm px-4 py-2 inline-flex items-center gap-1 disabled:opacity-60"
                        data-testid="msg-compose-send"
                    >
                        {sending ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                        Send message
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default MessageComposeDialog;
